import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import { Stethoscope, Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: email.trim() });
      setMessage(res.data.message);
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Stethoscope className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-slate-800">Guatemala Surgery</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl rounded-2xl">
          <CardHeader className="text-center pt-8 pb-4">
            {sent ? (
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="w-7 h-7 text-primary" />
              </div>
            )}
            <CardTitle className="text-2xl font-heading font-bold text-slate-800">
              {sent ? 'Check your email' : 'Forgot your password?'}
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1">
              {sent
                ? message
                : "Enter your email and we'll send you a reset link. Works for any email provider."}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8 pt-2">
            {!sent ? (
              <form noValidate onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="pl-10 h-12 rounded-xl"
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" className="w-full h-12 rounded-xl bg-primary" disabled={submitting} data-testid="forgot-submit-btn">
                  {submitting ? 'Sending...' : 'Send Reset Link'}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
                <p className="text-center text-sm text-slate-500">
                  <Link to="/login" className="text-primary hover:underline">← Back to Sign In</Link>
                </p>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500 text-center">
                  Didn't receive it? Check your spam folder, or{' '}
                  <button className="text-primary hover:underline" onClick={() => { setSent(false); setMessage(''); }}>
                    try again
                  </button>.
                </p>
                <Link to="/login">
                  <Button className="w-full rounded-xl bg-primary">Back to Sign In</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
